import { ModalityType } from '@littlebird/sdk';
import { selectReasoningDisplayEntries } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
} from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui';
import { loaderPortalId } from '@/components/ui/logo-loader';
import { MCPIcon } from '@/features/mcp/MCPIcon';
import { agentRunsStore } from '@/features/sprites/agent-runs.store';
import { terminalRunIsActive } from '@/features/sprites/terminal-run-card.utils';
import { createSafeContext } from '@/hooks/createContextStore.hooks';
import { EASE } from '@/lib/animate';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { classList } from '@/utils/cn';

import {
  getModalityDisplayLabelParts,
  ModalityStepContent,
  renderModalityExpandedContent,
  ThoughtContent,
} from './modality-registry';
import { ReasoningStep } from './reasoning-step';
import {
  getStepDisplayLabelParts,
  getStepDisplayMetadata,
  stepHasDetailContent,
} from './reasoning-step-display';
import {
  ReasoningStatus,
  ReasoningStepType,
  type ReasoningStep as ReasoningStepT,
} from './reasoning.types';
import {
  getIntegrationToolName,
  getStepTitle,
  isContentOldEnough,
} from './reasoning.utils';
import { ShimmerCollapsibleHeader } from './ShimmerCollapsibleHeader';
import { StepPreviewContent } from './step-preview-content';

type ReasoningPanelContextValue = {
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  steps: ReasoningStepT[];
  status: ReasoningStatus;
  hasExpandableContent: boolean;
  callCountByStepId: ReadonlyMap<string, number>;
};

const [ReasoningPanelContextProvider, useReasoningPanelContext] =
  createSafeContext<ReasoningPanelContextValue>(
    'ReasoningPanelContext must be used within a ReasoningPanelContextProvider'
  );

type ReasoningPanelProps = {
  id?: string;
  steps: ReasoningStepT[];
  status: ReasoningStatus;
  isCompact?: boolean;
  forceExpanded?: boolean;
};

const STAGGER_DELAY_MS = 1200;

function useStaggeredSteps(
  steps: ReasoningStepT[],
  status: ReasoningStatus
): ReasoningStepT[] {
  const [revealedCount, setRevealedCount] = useState(() =>
    status === ReasoningStatus.Complete
      ? steps.length
      : Math.min(1, steps.length)
  );
  const prevLengthRef = useRef(steps.length);

  useEffect(() => {
    if (status === ReasoningStatus.Complete) setRevealedCount(steps.length);
  }, [status, steps.length]);

  useEffect(() => {
    const prev = prevLengthRef.current;
    prevLengthRef.current = steps.length;
    if (status !== ReasoningStatus.Pending) return;
    if (steps.length - prev === 1) {
      setRevealedCount((current) => (current >= prev ? steps.length : current));
    } else if (steps.length - prev >= 2) {
      setRevealedCount((current) => Math.max(current, prev + 1));
    }
  }, [steps.length, status]);

  useEffect(() => {
    if (status !== ReasoningStatus.Pending) return;
    if (revealedCount >= steps.length) return;
    const timer = setTimeout(
      () => setRevealedCount((p) => p + 1),
      STAGGER_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [revealedCount, steps.length, status]);

  return steps.slice(0, Math.min(revealedCount, steps.length));
}

function useDisplaySteps(steps: ReasoningStepT[], status: ReasoningStatus) {
  const visibleSteps = useStaggeredSteps(steps, status);
  const displayEntries = selectReasoningDisplayEntries(
    visibleSteps,
    status,
    getStepDisplayMetadata
  );

  return {
    displaySteps: displayEntries.map(({ step }) => step),
    callCountByStepId: new Map(
      displayEntries.map(({ step, count }) => [
        step.id,
        userStore.isInternalUser ? count : 1,
      ])
    ),
  };
}

export const ReasoningPanel = observer(
  ({
    id,
    steps,
    status,
    isCompact = false,
    forceExpanded = false,
  }: ReasoningPanelProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { displaySteps, callCountByStepId } = useDisplaySteps(steps, status);

    if (displaySteps.length === 0) return null;

    const hasExpandableContent =
      displaySteps.length >= 2 ||
      stepHasDetailContent(displaySteps[0], status === ReasoningStatus.Pending);

    return (
      <ReasoningPanelContextProvider
        value={{
          isExpanded: isExpanded || forceExpanded,
          setIsExpanded,
          steps: displaySteps,
          status,
          hasExpandableContent,
          callCountByStepId,
        }}
      >
        <MotionConfig reducedMotion="user">
          <YStack
            data-message-id={id}
            data-compact={isCompact}
            data-testid="reasoning-panel"
            className={cn('group/reasoning relative w-full py-3', {
              'px-6': !isCompact,
            })}
          >
            {id && (
              <div
                id={loaderPortalId(id)}
                className={classList(
                  'absolute',
                  '-left-1.5',
                  'top-2.5',
                  'size-6',
                  'pointer-events-none'
                )}
              />
            )}
            <PanelHeader />
            <PanelPreview />
            <PanelContent />
          </YStack>
        </MotionConfig>
      </ReasoningPanelContextProvider>
    );
  }
);

const PanelPreview = () => {
  const { isExpanded, steps, status } = useReasoningPanelContext();
  const shouldReduceMotion = useReducedMotion();

  if (isExpanded || status === ReasoningStatus.Complete || steps.length === 0)
    return null;

  const latestStep = steps[steps.length - 1];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.18,
          ease: EASE.outQuint,
        }}
        className="overflow-hidden pl-1"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={latestStep.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: EASE.outQuint }}
          >
            <StepPreviewContent step={latestStep} />
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

const HeaderToolIcon = ({ toolName }: { toolName: string }) => (
  <span className="inline-flex shrink-0 items-center justify-center size-3.5 text-foreground-muted">
    <MCPIcon server={{ name: toolName }} className="size-3.5" />
  </span>
);

// observer: the terminal-run store decides whether the latest step is still live
const PanelHeader = observer(() => {
  const {
    isExpanded,
    setIsExpanded,
    status,
    steps,
    hasExpandableContent,
    callCountByStepId,
  } = useReasoningPanelContext();

  const label = () => {
    if (status === ReasoningStatus.Complete) {
      if (steps.length > 1) return `${steps.length} Steps`;
      const step = steps[0];
      const labelParts = getStepDisplayLabelParts(step);
      const stepLabel =
        step.type === ReasoningStepType.Modality &&
        step.modality.type === ModalityType.RunSubagent &&
        labelParts?.length === 2 ? (
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <span className="text-foreground-muted flex-shrink-0">
              {labelParts[0]}
            </span>
            <span className="truncate">{labelParts[1]}</span>
          </span>
        ) : (
          (labelParts?.join(' · ') ?? getStepTitle(step, false))
        );
      // Same icon as the pending header: it names the tool, it does not
      // signal activity (the shimmer does that), so it stays once done.
      const doneToolName = getIntegrationToolName(step);
      return (
        <span className="inline-flex items-center gap-3 min-w-0">
          {doneToolName && <HeaderToolIcon toolName={doneToolName} />}
          <span className="inline-flex items-center gap-1.5 min-w-0">
            {stepLabel}
            <ReasoningStep.CallCountSuffix
              count={callCountByStepId.get(step.id) ?? 1}
            />
          </span>
        </span>
      );
    }

    const latestStep = steps[steps.length - 1];

    // A terminal step whose command already exited while the message is still
    // streaming: the agent is reading the result, not using the terminal.
    if (
      latestStep.type === ReasoningStepType.Modality &&
      terminalRunIsActive(
        latestStep.modality,
        latestStep.id,
        agentRunsStore
      ) === false
    ) {
      return 'Thinking';
    }

    const toolName = getIntegrationToolName(latestStep);
    const activeLabel =
      latestStep.type === ReasoningStepType.Modality
        ? getModalityDisplayLabelParts(
            latestStep.modality,
            latestStep.toolCall,
            true,
            userStore.isInternalUser
          )?.join(' · ')
        : undefined;

    return (
      <span className="inline-flex items-center gap-3">
        {toolName && <HeaderToolIcon toolName={toolName} />}
        {activeLabel ?? getStepTitle(latestStep, true)}
      </span>
    );
  };

  const isDone = status === ReasoningStatus.Complete;

  return (
    <ShimmerCollapsibleHeader
      title={label()}
      isShimmering={!isDone}
      open={isExpanded}
      onOpenChange={setIsExpanded}
      showChevron={hasExpandableContent}
      data-testid="reasoning-panel-header"
    />
  );
});

const PanelContent = observer(() => {
  const { isExpanded, steps, status, hasExpandableContent, callCountByStepId } =
    useReasoningPanelContext();
  const shouldReduceMotion = useReducedMotion();

  if (!hasExpandableContent) return null;

  return (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{
            height: 0,
            transition: {
              duration: shouldReduceMotion ? 0 : 0.15,
              ease: EASE.outQuint,
            },
          }}
          transition={{
            duration: shouldReduceMotion ? 0 : 0.22,
            ease: EASE.outQuint,
          }}
          className="mt-3 overflow-hidden"
        >
          <StepRows
            steps={steps}
            status={status}
            callCountByStepId={callCountByStepId}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export const ReasoningStepList = observer(
  ({ steps, status }: { steps: ReasoningStepT[]; status: ReasoningStatus }) => {
    const { displaySteps, callCountByStepId } = useDisplaySteps(steps, status);

    if (displaySteps.length === 0) return null;

    return (
      <StepRows
        steps={displaySteps}
        status={status}
        callCountByStepId={callCountByStepId}
      />
    );
  }
);

const StepRows = observer(
  ({
    steps,
    status,
    callCountByStepId,
  }: {
    steps: ReasoningStepT[];
    status: ReasoningStatus;
    callCountByStepId: ReadonlyMap<string, number>;
  }) => (
    <YStack className="pl-1">
      <AnimatePresence initial={false}>
        {steps.map((step, stepIndex) => {
          const isNew = !isContentOldEnough(step.createdAt, 500);
          const last = stepIndex === steps.length - 1;
          const messageActive = last && status === ReasoningStatus.Pending;
          // A terminal step knows when its own command ended; prefer that.
          const isActive =
            (step.type === ReasoningStepType.Modality
              ? terminalRunIsActive(step.modality, step.id, agentRunsStore)
              : null) ?? messageActive;

          if (step.type === ReasoningStepType.Thought) {
            if (steps.length === 1) {
              return (
                <ThoughtContent
                  key={step.id}
                  thought={step.hiddenContent}
                  id={step.id}
                />
              );
            }
            return (
              <ReasoningStep.Root
                last={last}
                isNew={isNew}
                isActive={isActive}
                key={step.id}
                defaultOpen={false}
              >
                <ReasoningStep.Preview>
                  <ReasoningStep.PreviewIcon>
                    <Icons.thought className="size-3.5" />
                  </ReasoningStep.PreviewIcon>
                  <ReasoningStep.PreviewText>
                    {getStepTitle(step, isActive)}
                  </ReasoningStep.PreviewText>
                </ReasoningStep.Preview>
                <ReasoningStep.Content>
                  <ThoughtContent thought={step.hiddenContent} id={step.id} />
                </ReasoningStep.Content>
              </ReasoningStep.Root>
            );
          }

          if (step.type === ReasoningStepType.Modality) {
            const modality = step.modality;
            const canExpand = stepHasDetailContent(step, isActive);

            if (steps.length === 1) {
              const expandedContent = renderModalityExpandedContent(modality, {
                isActive,
                toolCall: step.toolCall,
              });
              if (expandedContent) {
                return (
                  <div key={step.id} className="text-sm text-foreground-muted">
                    {expandedContent}
                  </div>
                );
              }
            }

            return (
              <ReasoningStep.Root
                key={step.id}
                last={last}
                isNew={isNew}
                isActive={isActive}
                isLiveTodo={step.isLiveTodo}
                toolCall={step.toolCall}
                stepId={step.id}
                defaultOpen={
                  modality.type !== ModalityType.TodoWrite &&
                  modality.type !== ModalityType.RunSubagent
                }
                canExpand={canExpand}
                callCount={callCountByStepId.get(step.id) ?? 1}
                dataModalityType={modality.type}
              >
                <ModalityStepContent modality={modality} />
              </ReasoningStep.Root>
            );
          }
          return null;
        })}
      </AnimatePresence>
    </YStack>
  )
);
